import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Gauge, Timer, CheckCircle2, AlertCircle,
  Info, Save, RotateCcw, Microscope, BarChart3, Zap,
  Thermometer, ArrowDown, ArrowRightLeft, Volume2,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets, Database,
  FlaskConical, Settings, Beaker, ArrowRight, Filter
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

interface BufferParams {
  flowRate: number;       // m³/day
  peakFactor: number;     // ×
  minFlow: number;        // m³/day
  influentBOD: number;    // mg/L
  influentCOD: number;    // mg/L
  influentTSS: number;    // mg/L
  temperature: number;    // °C
  og: number; ph: number; alkalinity: number; turbidity: number;
  conductivity: number; hardness: number; nh3n: number; tp: number;
  tankLength: number;     // m
  tankWidth: number;      // m
  tankDepth: number;      // m
  freeboard: number;      // m
  workingVolumePercent: number;
  pumpCapacity: number;   // m³/h
  highLevelSetpoint: number; // %
  lowLevelSetpoint: number;  // %
  mixerPower: number;     // kW
}

interface BufferSize {
  totalVolume: number;
  workingVolume: number;
  freeboardVolume: number;
  tankArea: number;
  detentionTime: number;
  peakDetention: number;
  volumeTurnover: number;
  surgeVolume: number;
  surgeRequired: boolean;
  fillTimeAtPeak: number;   // h
  emptyTimeAtMin: number; // h
  pumpRunTime: number;    // min/cycle
  pumpStartsPerDay: number;
  dryRunRisk: string;
  gValue: number;
  mixingTime: number;
  massBalance: number;     // m³/day
  overflowRisk: number;    // %
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'Moderate buffering, steady pH', icon: Milk,
    defaults: { flowRate: 250, peakFactor: 2.0, minFlow: 80, influentBOD: 1850, influentCOD: 3200, influentTSS: 450, temperature: 25,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankLength: 6, tankWidth: 3, tankDepth: 2.5, freeboard: 0.5, workingVolumePercent: 85,
      pumpCapacity: 15, highLevelSetpoint: 85, lowLevelSetpoint: 25, mixerPower: 1.5 } },
  { name: 'Brewery Effluent', description: 'High buffering for batch discharge', icon: Factory,
    defaults: { flowRate: 350, peakFactor: 3.5, minFlow: 50, influentBOD: 2200, influentCOD: 4100, influentTSS: 620, temperature: 20,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankLength: 10, tankWidth: 5, tankDepth: 3, freeboard: 0.5, workingVolumePercent: 85,
      pumpCapacity: 25, highLevelSetpoint: 90, lowLevelSetpoint: 20, mixerPower: 3 } },
  { name: 'Fish Processing', description: 'Small buffer for dosing control', icon: FishSymbol,
    defaults: { flowRate: 150, peakFactor: 1.8, minFlow: 60, influentBOD: 1450, influentCOD: 2650, influentTSS: 520, temperature: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankLength: 4, tankWidth: 2, tankDepth: 2, freeboard: 0.5, workingVolumePercent: 85,
      pumpCapacity: 8, highLevelSetpoint: 80, lowLevelSetpoint: 30, mixerPower: 1 } },
];

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

export function calcBuffer(p: BufferParams): BufferSize {
  const Q = p.flowRate;
  const q = Q / 24;
  const peakQ = q * p.peakFactor;
  const minQ = p.minFlow / 24;

  const tankArea = p.tankLength * p.tankWidth;
  const totalVolume = tankArea * (p.tankDepth + p.freeboard);
  const workingVolume = tankArea * p.tankDepth * (p.workingVolumePercent / 100);
  const freeboardVolume = totalVolume - workingVolume;

  const detentionTime = workingVolume / q;
  const peakDetention = workingVolume / peakQ;
  const volumeTurnover = Q / workingVolume;

  const peakDuration = 2;
  const surgeVolume = (peakQ - q) * peakDuration;
  const surgeRequired = surgeVolume > 0.2 * workingVolume;

  const fillTimeAtPeak = workingVolume / peakQ;
  const emptyTimeAtMin = workingVolume / minQ;

  const usableVolume = workingVolume * ((p.highLevelSetpoint - p.lowLevelSetpoint) / 100);
  const pumpCyclesPerDay = usableVolume > 0 ? Q / usableVolume : 0;
  const pumpRunTime = usableVolume > 0 ? (usableVolume / p.pumpCapacity) * 60 : 0;

  const dailyOutflow = p.pumpCapacity * 24;
  const massBalance = Q - dailyOutflow;
  const overflowRisk = massBalance > 0 ? Math.min(100, (massBalance / workingVolume) * 100) : 0;

  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (p.temperature - 20) / tempK);
  const mixerPowerW = p.mixerPower * 1000;
  const gValue = p.mixerPower > 0 ? Math.sqrt(mixerPowerW / (mu * workingVolume)) : 0;
  const mixingTime = workingVolume > 0 ? (workingVolume / q) * 60 : 0;

  const dryRunRisk = p.pumpCapacity > q * 1.5 ? 'low' : p.pumpCapacity < q ? 'high' : 'moderate';

  let status: BufferSize['status']; let statusDetail: string;
  const designNotes: string[] = [];

  if (detentionTime >= 4 && peakDetention >= 1 && volumeTurnover <= 6 && overflowRisk < 10) {
    status = 'optimal';
    statusDetail = `Optimal. Detention ${detentionTime.toFixed(1)} h, turnover ${volumeTurnover.toFixed(1)}×/d, overflow risk ${overflowRisk.toFixed(0)}%.`;
  } else if (detentionTime >= 2 && peakDetention >= 0.5 && volumeTurnover <= 10 && overflowRisk < 25) {
    status = 'acceptable';
    statusDetail = `Acceptable. Detention ${detentionTime.toFixed(1)} h. Monitor levels.`;
  } else if (detentionTime < 0.5 || overflowRisk > 50 || volumeTurnover > 15) {
    status = 'overloaded';
    statusDetail = `Overloaded. Detention ${detentionTime.toFixed(1)} h or overflow risk ${overflowRisk.toFixed(0)}%. Increase volume or pump capacity.`;
  } else if (totalVolume < 10) {
    status = 'undersized';
    statusDetail = `Undersized. Working volume ${workingVolume.toFixed(1)} m³. Increase tank size.`;
  } else {
    status = 'oversized';
    statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  if (overflowRisk > 25) designNotes.push('⚠️ Overflow risk high. Increase pump capacity or tank volume.');
  if (dryRunRisk === 'high') designNotes.push('⚠️ Pump capacity below average flow. Pump will run dry.');
  if (pumpCyclesPerDay > 30) designNotes.push('⚠️ Excessive pump cycling. Increase usable volume or pump capacity.');
  if (surgeRequired) designNotes.push('ℹ️ Surge volume exceeds 20% of working volume. Consider larger tank.');
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    totalVolume, workingVolume, freeboardVolume, tankArea,
    detentionTime, peakDetention, volumeTurnover,
    surgeVolume, surgeRequired,
    fillTimeAtPeak, emptyTimeAtMin,
    pumpRunTime, pumpStartsPerDay: pumpCyclesPerDay,
    dryRunRisk,
    gValue, mixingTime,
    massBalance, overflowRisk,
    status, statusDetail, designNotes,
  };
}

/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : safeValue.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{fmt(safeValue, 1)} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

/* ─── Flow Diagram ───────────────────────────────────── */
function FlowDiagram({ params, size }: { params: BufferParams; size: BufferSize }) {
  const safe = {
    Q: typeof params.flowRate === 'number' ? params.flowRate / 24 : 0,
    peakQ: typeof params.flowRate === 'number' ? (params.flowRate * params.peakFactor) / 24 : 0,
    det: typeof size?.detentionTime === 'number' ? size.detentionTime : 0,
    turnover: typeof size?.volumeTurnover === 'number' ? size.volumeTurnover : 0,
    gVal: typeof size?.gValue === 'number' ? size.gValue : 0,
    pumpRun: typeof size?.pumpRunTime === 'number' ? size.pumpRunTime : 0,
    pumpCycles: typeof size?.pumpStartsPerDay === 'number' ? size.pumpStartsPerDay : 0,
  };

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — Influent → Buffer → Pump → Process
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">BUFFER TANK — SURGE PROTECTION & PUMP CONTROL</text>

        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradB)" stroke="#f59e0b" strokeWidth="2" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Q avg {fmt(params.flowRate, 0)} m³/d</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">Q peak {fmt(params.flowRate * params.peakFactor, 0)} m³/d</text>
        <text x="80" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">BOD {fmt(params.influentBOD, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">COD {fmt(params.influentCOD, 0)} mg/L</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#f59e0b" />

        {/* Buffer Tank */}
        <rect x="180" y="40" width="250" height="110" rx="6" fill="url(#waterGradB)" stroke="#06b6d4" strokeWidth="2" />
        <text x="305" y="58" fill="#06b6d4" fontSize="10" textAnchor="middle" fontWeight="bold">Buffer Tank</text>
        <text x="305" y="74" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.tankLength, 1)} × {fmt(params.tankWidth, 1)} × {fmt(params.tankDepth, 1)} m | Vol {fmt(size.workingVolume, 0)} m³</text>
        <text x="305" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Detention {fmt(safe.det, 1)} h | Turnover {fmt(safe.turnover, 1)}×/d</text>
        <text x="305" y="102" fill="#f59e0b" fontSize="8" textAnchor="middle">Pump cycles {fmt(safe.pumpCycles, 0)}/d | Run {fmt(safe.pumpRun, 0)} min/cycle</text>
        <text x="305" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">Level {fmt(params.lowLevelSetpoint, 0)}%–{fmt(params.highLevelSetpoint, 0)}%</text>

        {/* Level indicators */}
        <rect x="195" y="55" width="4" height="80" rx="1" fill="var(--rb-active)" />
        <rect x="195" y={135 - (params.highLevelSetpoint / 100) * 80} width="4" height={(params.highLevelSetpoint - params.lowLevelSetpoint) / 100 * 80} rx="1" fill="#06b6d4" opacity="0.5" />
        <line x1="190" y1={135 - (params.lowLevelSetpoint / 100) * 80} x2="205" y2={135 - (params.lowLevelSetpoint / 100) * 80} stroke="#06b6d4" strokeWidth="0.5" />
        <line x1="190" y1={135 - (params.highLevelSetpoint / 100) * 80} x2="205" y2={135 - (params.highLevelSetpoint / 100) * 80} stroke="#06b6d4" strokeWidth="0.5" />

        {/* Mixer */}
        {params.mixerPower > 0 && (
          <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3, ease: "linear" }} style={{ transformOrigin: '240px 100px' }}>
            <circle cx="240" cy="100" r="8" fill="none" stroke="#06b6d4" strokeWidth="1" />
            <line x1="240" y1="92" x2="240" y2="108" stroke="#06b6d4" strokeWidth="1" />
            <line x1="232" y1="100" x2="248" y2="100" stroke="#06b6d4" strokeWidth="1" />
          </motion.g>
        )}

        <line x1="432" y1="95" x2="470" y2="95" stroke="#06b6d4" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="468,92 475,95 468,98" fill="#06b6d4" />

        {/* Pump */}
        <g transform="translate(480, 75)">
          <path d="M 0,15 Q 0,0 12,0 Q 24,0 28,10 Q 32,20 22,28 Q 12,36 4,30" fill="#0f172a" stroke="#0288D1" strokeWidth="1.5" />
          <rect x="-6" y="8" width="6" height="8" rx="1" fill="var(--rb-active)" stroke="#0288D1" strokeWidth="1" />
          <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} style={{ transformOrigin: '14px 15px' }}>
            <line x1="14" y1="7" x2="14" y2="23" stroke="#0288D1" strokeWidth="1.2" />
            <line x1="6" y1="15" x2="22" y2="15" stroke="#0288D1" strokeWidth="1.2" />
          </motion.g>
          <text x="14" y="40" fill="#0288D1" fontSize="7" textAnchor="middle">Pump {fmt(params.pumpCapacity, 0)} m³/h</text>
        </g>

        <line x1="510" y1="90" x2="560" y2="90" stroke="#0288D1" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="558,87 565,90 558,93" fill="#0288D1" />

        <rect x="570" y="55" width="100" height="80" rx="6" fill="url(#waterGradB)" stroke="#10b981" strokeWidth="2" />
        <text x="620" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">To Process</text>
        <text x="620" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Controlled flow</text>
        <text x="620" y="100" fill="#10b981" fontSize="8" textAnchor="middle">Q pump {fmt(params.pumpCapacity, 0)} m³/h</text>
        <text x="620" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Balance {fmt(size.massBalance, 0)} m³/d</text>

        {/* Overflow risk indicator */}
        <rect x="690" y="55" width="180" height="100" rx="6" fill="#0f172a" stroke={size.overflowRisk > 25 ? '#ef4444' : size.overflowRisk > 10 ? '#f59e0b' : '#10b981'} strokeWidth="1" />
        <text x="780" y="72" fill={size.overflowRisk > 25 ? '#ef4444' : size.overflowRisk > 10 ? '#f59e0b' : '#10b981'} fontSize="9" textAnchor="middle" fontWeight="bold">Tank Status</text>
        <text x="780" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">Overflow risk: {fmt(size.overflowRisk, 0)}%</text>
        <text x="780" y="102" fill="#94a3b8" fontSize="8" textAnchor="middle">Dry run risk: {size.dryRunRisk}</text>
        <text x="780" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">Surge: {size.surgeRequired ? 'Required' : 'OK'}</text>
        <text x="780" y="130" fill="#94a3b8" fontSize="8" textAnchor="middle">G-value: {fmt(safe.gVal, 0)} s⁻¹</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#f59e0b" fontSize="9">— Influent (variable)</text>
        <text x="155" y="359" fill="#06b6d4" fontSize="9">— Buffer + Mixing</text>
        <text x="270" y="359" fill="#0288D1" fontSize="9">— Pump Control</text>
        <text x="375" y="359" fill="#10b981" fontSize="9">— To Process</text>
        <text x="480" y="359" fill="#94a3b8" fontSize="9">Det={fmt(safe.det, 1)}h | Cycles={fmt(safe.pumpCycles, 0)}/d | G={fmt(safe.gVal, 0)}s⁻¹</text>
      </svg>
    </div>
  );
}

export default function BufferSpecPage() {
  const [params, setParams] = useState<BufferParams>(PROFILES[0].defaults);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'pump'>('influent');
  const [profileIndex, setProfileIndex] = useState(0);

  const size = useMemo(() => calcBuffer(params), [params]);
  const sampleLoader = useSampleLoader<BufferParams>(setParams);

  const handleReset = () => { setParams(PROFILES[0].defaults); setProfileIndex(0); sampleLoader.clearSample(); toast.info('Parameters reset to defaults'); };
  const handleSave = () => { localStorage.setItem('buffer-params-v2', JSON.stringify(params)); setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Configuration saved'); };

  useEffect(() => {
    const savedParams = localStorage.getItem('buffer-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const Q = params.flowRate;
    const q = Q / 24;
    const peakQ = q * params.peakFactor;
    const minQ = params.minFlow / 24;
    const tankArea = params.tankLength * params.tankWidth;
    const totalVolume = tankArea * (params.tankDepth + params.freeboard);
    const workingVolume = tankArea * params.tankDepth * (params.workingVolumePercent / 100);
    const freeboardVolume = totalVolume - workingVolume;
    const detentionTime = workingVolume / q;
    const peakDetention = workingVolume / peakQ;
    const volumeTurnover = Q / workingVolume;
    const surgeVolume = (peakQ - q) * 2;
    const surgeRequired = surgeVolume > 0.2 * workingVolume;
    const usableVolume = workingVolume * ((params.highLevelSetpoint - params.lowLevelSetpoint) / 100);
    const pumpCyclesPerDay = usableVolume > 0 ? Q / usableVolume : 0;
    const pumpRunTime = usableVolume > 0 ? (usableVolume / params.pumpCapacity) * 60 : 0;
    const fillTimeAtPeak = workingVolume / peakQ;
    const emptyTimeAtMin = workingVolume / minQ;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.exp(-1.5 * (params.temperature - 20) / tempK);
    const mixerPowerW = params.mixerPower * 1000;
    const gValue = params.mixerPower > 0 ? Math.sqrt(mixerPowerW / (mu * workingVolume)) : 0;
    const mixingTime = workingVolume > 0 ? (workingVolume / q) * 60 : 0;
    const dailyOutflow = params.pumpCapacity * 24;
    const massBalance = Q - dailyOutflow;
    const overflowRisk = massBalance > 0 ? Math.min(100, (massBalance / workingVolume) * 100) : 0;
    const dryRunRisk = params.pumpCapacity > q * 1.5 ? 'low' : params.pumpCapacity < q ? 'high' : 'moderate';
    return [
      {
        title: 'Hydraulics & Volume',
        icon: Waves,
        equations: [
          { label: 'Average flow (hourly)', formula: 'q = flowRate / 24', substitution: `q = ${sharedFmt(Q, 0)} / 24 = ${sharedFmt(q, 1)} m³/h`, result: sharedFmt(q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Peak flow (hourly)', formula: 'q_peak = q × peakFactor', substitution: `q_peak = ${sharedFmt(q, 1)} × ${params.peakFactor} = ${sharedFmt(peakQ, 1)} m³/h`, result: sharedFmt(peakQ, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Min flow (hourly)', formula: 'q_min = minFlow / 24', substitution: `q_min = ${params.minFlow} / 24 = ${sharedFmt(minQ, 1)} m³/h`, result: sharedFmt(minQ, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Tank area', formula: 'A = L × W', substitution: `A = ${params.tankLength} × ${params.tankWidth} = ${sharedFmt(tankArea, 1)} m²`, result: sharedFmt(tankArea, 1), status: 'pass', unit: 'm²' },
          { label: 'Total volume', formula: 'V_total = A × (depth + freeboard)', substitution: `V_total = ${sharedFmt(tankArea, 1)} × (${params.tankDepth} + ${params.freeboard}) = ${sharedFmt(totalVolume, 1)} m³`, result: sharedFmt(totalVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Working volume', formula: 'V_work = A × depth × (working% / 100)', substitution: `V_work = ${sharedFmt(tankArea, 1)} × ${params.tankDepth} × ${params.workingVolumePercent / 100} = ${sharedFmt(workingVolume, 1)} m³`, result: sharedFmt(workingVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Freeboard volume', formula: 'V_fb = V_total − V_work', substitution: `V_fb = ${sharedFmt(totalVolume, 1)} − ${sharedFmt(workingVolume, 1)} = ${sharedFmt(freeboardVolume, 1)} m³`, result: sharedFmt(freeboardVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Detention time', formula: 't = V_work / q', substitution: `t = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 1)} = ${sharedFmt(detentionTime, 1)} h`, result: sharedFmt(detentionTime, 1), status: detentionTime >= 4 ? 'pass' : detentionTime >= 2 ? 'warn' : 'fail', unit: 'h' },
          { label: 'Peak detention', formula: 't_peak = V_work / q_peak', substitution: `t_peak = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(peakQ, 1)} = ${sharedFmt(peakDetention, 1)} h`, result: sharedFmt(peakDetention, 1), status: peakDetention >= 1 ? 'pass' : peakDetention >= 0.5 ? 'warn' : 'fail', unit: 'h' },
          { label: 'Volume turnover', formula: 'turnover = Q / V_work', substitution: `turnover = ${sharedFmt(Q, 0)} / ${sharedFmt(workingVolume, 1)} = ${sharedFmt(volumeTurnover, 1)} ×/d`, result: sharedFmt(volumeTurnover, 1), status: volumeTurnover <= 6 ? 'pass' : volumeTurnover <= 10 ? 'warn' : 'fail', unit: '×/d' },
          { label: 'Surge volume', formula: 'V_surge = (q_peak − q) × 2 h', substitution: `V_surge = (${sharedFmt(peakQ, 1)} − ${sharedFmt(q, 1)}) × 2 = ${sharedFmt(surgeVolume, 1)} m³`, result: sharedFmt(surgeVolume, 1), status: !surgeRequired ? 'pass' : 'warn', unit: 'm³' },
        ],
      },
      {
        title: 'Pump Control',
        icon: Gauge,
        equations: [
          { label: 'Usable volume', formula: 'V_use = V_work × (high% − low%) / 100', substitution: `V_use = ${sharedFmt(workingVolume, 1)} × (${params.highLevelSetpoint} − ${params.lowLevelSetpoint}) / 100 = ${sharedFmt(usableVolume, 1)} m³`, result: sharedFmt(usableVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Pump cycles/day', formula: 'N = Q / V_use', substitution: `N = ${sharedFmt(Q, 0)} / ${sharedFmt(usableVolume, 1)} = ${sharedFmt(pumpCyclesPerDay, 0)}`, result: sharedFmt(pumpCyclesPerDay, 0), status: pumpCyclesPerDay <= 20 ? 'pass' : pumpCyclesPerDay <= 30 ? 'warn' : 'fail', unit: '/d' },
          { label: 'Pump run time', formula: 't_run = (V_use / pumpCapacity) × 60', substitution: `t_run = (${sharedFmt(usableVolume, 1)} / ${params.pumpCapacity}) × 60 = ${sharedFmt(pumpRunTime, 0)} min`, result: sharedFmt(pumpRunTime, 0), status: 'pass', unit: 'min' },
          { label: 'Fill time at peak', formula: 't_fill = V_work / q_peak', substitution: `t_fill = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(peakQ, 1)} = ${sharedFmt(fillTimeAtPeak, 1)} h`, result: sharedFmt(fillTimeAtPeak, 1), status: 'pass', unit: 'h' },
          { label: 'Empty time at min', formula: 't_empty = V_work / q_min', substitution: `t_empty = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(minQ, 1)} = ${sharedFmt(emptyTimeAtMin, 1)} h`, result: sharedFmt(emptyTimeAtMin, 1), status: 'pass', unit: 'h' },
        ],
      },
      {
        title: 'Mixing & Risks',
        icon: FlaskConical,
        equations: [
          { label: 'Water viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]', substitution: `μ = 0.001 × exp[−1.5×${params.temperature - 20}/${sharedFmt(tempK, 1)}] = ${sharedFmt(mu * 1000, 3)} mPa·s`, result: sharedFmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
          { label: 'G-value (velocity gradient)', formula: 'G = √(P / (μ × V_work))', substitution: `G = √(${sharedFmt(mixerPowerW, 0)} / (${sharedFmt(mu, 5)} × ${sharedFmt(workingVolume, 1)})) = ${sharedFmt(gValue, 0)} s⁻¹`, result: sharedFmt(gValue, 0), status: gValue > 20 && gValue < 100 ? 'pass' : gValue > 0 ? 'warn' : 'fail', unit: 's⁻¹' },
          { label: 'Mixing time', formula: 't_mix = (V_work / q) × 60', substitution: `t_mix = (${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 1)}) × 60 = ${sharedFmt(mixingTime, 0)} min`, result: sharedFmt(mixingTime, 0), status: 'pass', unit: 'min' },
          { label: 'Daily outflow', formula: 'Q_out = pumpCapacity × 24', substitution: `Q_out = ${params.pumpCapacity} × 24 = ${sharedFmt(dailyOutflow, 0)} m³/d`, result: sharedFmt(dailyOutflow, 0), status: 'pass', unit: 'm³/d' },
          { label: 'Mass balance', formula: 'ΔQ = Q − Q_out', substitution: `ΔQ = ${sharedFmt(Q, 0)} − ${sharedFmt(dailyOutflow, 0)} = ${sharedFmt(massBalance, 0)} m³/d`, result: sharedFmt(massBalance, 0), status: massBalance <= 0 ? 'pass' : massBalance <= 50 ? 'warn' : 'fail', unit: 'm³/d' },
          { label: 'Overflow risk', formula: 'risk = min(100, (ΔQ / V_work) × 100)', substitution: `risk = min(100, (${sharedFmt(massBalance, 0)} / ${sharedFmt(workingVolume, 1)}) × 100) = ${sharedFmt(overflowRisk, 0)}%`, result: sharedFmt(overflowRisk, 0), status: overflowRisk < 10 ? 'pass' : overflowRisk < 25 ? 'warn' : 'fail', unit: '%' },
          { label: 'Dry run risk', formula: 'pumpCapacity > q×1.5 ? low : pumpCapacity < q ? high : moderate', substitution: `${params.pumpCapacity} > ${sharedFmt(q * 1.5, 1)} ? ${dryRunRisk}`, result: dryRunRisk, status: dryRunRisk === 'low' ? 'pass' : dryRunRisk === 'moderate' ? 'warn' : 'fail', unit: '' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Waves}
        iconColor="text-emerald-400"
        title="Buffer Tank Specification"
        subtitle="Surge protection, pump control, and flow balancing with mass balance physics"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=buffer')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      <div className={`rounded-xl border p-4 ${sc.bg} ${sc.border}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.bg}`}><StatusIcon className={`w-5 h-5 ${sc.text}`} /></div>
          <div><p className={`text-sm font-bold ${sc.text}`}>{size.status.toUpperCase()}</p><p className={`text-xs ${sc.text} opacity-80`}>{size.statusDetail}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROFILES.map((profile, i) => (
          <PC key={i} profile={profile} isActive={profileIndex === i} onClick={() => { setParams(profile.defaults); setProfileIndex(i); toast.info(`Loaded ${profile.name} profile`); }} />
        ))}
      </div>

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'BOD', value: `${sharedFmt(params.influentBOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.bod || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-emerald-400" />Process Flow</h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1"><Settings className="w-3 h-3" />{showFlow ? 'Collapse' : 'Expand'}</button>
          </div>
          <AnimatePresence>{showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}</AnimatePresence>
        </div>

        <div className="xl:col-span-5 space-y-4">
          <div className="p-4 rb-card">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" />Performance Gauges</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <G value={size.detentionTime} max={12} label="Detention" unit="h" color="#10b981" />
              <G value={size.volumeTurnover} max={15} label="Turnover" unit="×/d" color="#f59e0b" />
              <G value={size.pumpStartsPerDay} max={30} label="Pump Cycles" unit="/d" color="#06b6d4" />
              <G value={size.overflowRisk} max={100} label="Overflow Risk" unit="%" color="#ef4444" />
              <G value={size.gValue} max={100} label="G-Value" unit="s⁻¹" color="#a855f7" />
              <G value={size.massBalance} max={200} label="Mass Balance" unit="m³/d" color="#f97316" />
              <G value={size.pumpRunTime} max={60} label="Pump Run" unit="min" color="#22c55e" />
              <G value={size.mixerPower || 0} max={10} label="Mixer" unit="kW" color="#8b5cf6" />
            </div>
          </div>

          <div className="overflow-hidden rb-card">
            <div className="flex border-b border-slate-800">
              {(['influent', 'tank', 'pump'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${activeTab === tab ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300'}`}>
                  {tab === 'influent' ? 'Influent' : tab === 'tank' ? 'Tank Design' : 'Pump Control'}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'influent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4" onChange={(v: number) => setParams({ ...params, flowRate: v })} />
                  <S label="Peak Factor" value={params.peakFactor} min={1.2} max={5} step={0.1} unit="×" icon={ArrowRightLeft} color="#f59e0b" onChange={(v: number) => setParams({ ...params, peakFactor: v })} />
                  <S label="Min Flow" value={params.minFlow} min={10} max={500} step={5} unit="m³/day" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, minFlow: v })} />
                  <S label="Influent BOD" value={params.influentBOD} min={100} max={5000} step={50} unit="mg/L" icon={FlaskConical} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentBOD: v })} />
                  <S label="Influent COD" value={params.influentCOD} min={200} max={8000} step={100} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentCOD: v })} />
                  <S label="Influent TSS" value={params.influentTSS} min={50} max={2000} step={50} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentTSS: v })} />
                  <S label="Temperature" value={params.temperature} min={5} max={35} step={1} unit="°C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temperature: v })} />
                  <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="Influent pH" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} />
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Tank Length" value={params.tankLength} min={2} max={15} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankLength: v })} />
                  <S label="Tank Width" value={params.tankWidth} min={1.5} max={8} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankWidth: v })} />
                  <S label="Tank Depth" value={params.tankDepth} min={1.5} max={5} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankDepth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={Volume2} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="Working Vol %" value={params.workingVolumePercent} min={60} max={95} step={5} unit="%" icon={Gauge} color="#06b6d4" onChange={(v: number) => setParams({ ...params, workingVolumePercent: v })} />
                  <S label="Mixer Power" value={params.mixerPower} min={0} max={10} step={0.1} unit="kW" icon={Zap} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, mixerPower: v })} />
                </div>
              )}
              {activeTab === 'pump' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Pump Capacity" value={params.pumpCapacity} min={1} max={50} step={1} unit="m³/h" icon={ArrowRightLeft} color="#0288D1" onChange={(v: number) => setParams({ ...params, pumpCapacity: v })} />
                  <S label="High Level" value={params.highLevelSetpoint} min={50} max={95} step={5} unit="%" icon={ArrowRight} color="#0288D1" onChange={(v: number) => setParams({ ...params, highLevelSetpoint: v })} />
                  <S label="Low Level" value={params.lowLevelSetpoint} min={5} max={50} step={5} unit="%" icon={ArrowDown} color="#0288D1" onChange={(v: number) => setParams({ ...params, lowLevelSetpoint: v })} />
                </div>
              )}
            </div>
          </div>

          <div className="p-4 space-y-4 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" />Results & Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Hydraulics</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Detention</span><span className="text-white font-bold">{fmt(size.detentionTime, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Peak detention</span><span className="text-white font-bold">{fmt(size.peakDetention, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Turnover</span><span className="text-emerald-400 font-bold">{fmt(size.volumeTurnover, 1)} ×/day</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Surge volume</span><span className="text-emerald-400 font-bold">{fmt(size.surgeVolume, 1)} m³</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Pump Control</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Cycles/day</span><span className="text-sky-400 font-bold">{fmt(size.pumpStartsPerDay, 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Run time</span><span className="text-sky-400 font-bold">{fmt(size.pumpRunTime, 0)} min</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Fill time</span><span className="text-sky-400 font-bold">{fmt(size.fillTimeAtPeak, 1)} h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Empty time</span><span className="text-sky-400 font-bold">{fmt(size.emptyTimeAtMin, 1)} h</span></div>
                </div>
              </div>
              <div className="p-3 space-y-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Risks & Balance</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Overflow risk</span><span className={`${size.overflowRisk > 25 ? 'text-rose-400' : size.overflowRisk > 10 ? 'text-amber-400' : 'text-emerald-400'} font-bold`}>{fmt(size.overflowRisk, 0)} %</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Dry run risk</span><span className={`${size.dryRunRisk === 'high' ? 'text-rose-400' : size.dryRunRisk === 'moderate' ? 'text-amber-400' : 'text-emerald-400'} font-bold`}>{size.dryRunRisk}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Mass balance</span><span className="text-white font-bold">{fmt(size.massBalance, 0)} m³/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">G-value</span><span className="text-sky-400 font-bold">{fmt(size.gValue, 0)} s⁻¹</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes & Warnings</h3>
            <div className="space-y-2">
              {size.designNotes.map((note, i) => (
                <div key={i} className={`text-xs p-2 rounded-lg ${note.startsWith('⚠️') ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400' : note.startsWith('ℹ️') ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400' : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'}`}>{note}</div>
              ))}
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
